<template>
    <!-- MBR095 FAQ — no reference design; follows the Contact Us (MBR087) layout -->
    <div class="page-header align-items-start min-vh-100 mbr-faq">
        <span class="mask bg-white"></span>
        <div class="container p-0">
            <header class="mbr-faq__bar">
                <button type="button" class="mbr-faq__back" :aria-label="$t('common.back')" @click="goBack()">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
                    </svg>
                </button>
                <h1 class="mbr-faq__title">{{ $t('faq.title') }}</h1>
            </header>

            <main class="mbr-faq__body">
                <p class="mbr-faq__intro">{{ $t('faq.intro') }}</p>

                <!-- one question open at a time -->
                <div class="mbr-faq__list">
                    <div
                        v-for="(item, index) in faqs"
                        :key="index"
                        class="mbr-faq__item"
                        :class="{ 'mbr-faq__item--open': openIndex === index }"
                    >
                        <button
                            :id="`faq-q-${index}`"
                            type="button"
                            class="mbr-faq__question"
                            :aria-expanded="openIndex === index ? 'true' : 'false'"
                            :aria-controls="`faq-a-${index}`"
                            @click="toggle(index)"
                        >
                            <span class="mbr-faq__question-text">{{ item.q }}</span>
                            <svg class="mbr-faq__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6" />
                            </svg>
                        </button>
                        <div
                            :id="`faq-a-${index}`"
                            class="mbr-faq__answer"
                            role="region"
                            :aria-labelledby="`faq-q-${index}`"
                        >
                            <div class="mbr-faq__answer-inner">
                                <p class="mbr-faq__answer-text">{{ item.a }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <section v-if="canContact" class="mbr-faq__help">
                    <h2 class="mbr-faq__help-title">{{ $t('faq.help_title') }}</h2>
                    <p class="mbr-faq__help-text">{{ $t('faq.help_text') }}</p>
                    <button type="button" class="mbr-faq__help-btn" @click="goContact()">{{ $t('faq.contact_us') }}</button>
                </section>
            </main>
        </div>
    </div>
</template>

<script>
import { mapMutations } from "vuex";
import MemberService from "../services/member.service";
import TokenService from "../services/token.service";
import { getMerchantCode } from "@/config/utils";

// q1..q10 / a1..a10 in the faq namespace
const FAQ_COUNT = 10;

export default {
    name: "faq",
    data() {
        return {
            openIndex: null,
            memberPermissions: [],
            merchantCode: "",
        };
    },
    computed: {
        // computed (not data) so the Q&A follow the selected language
        faqs() {
            return Array.from({ length: FAQ_COUNT }, (_, i) => ({
                q: this.$t(`faq.q${i + 1}`),
                a: this.$t(`faq.a${i + 1}`),
            }));
        },
        // the Contact Us route rejects members without the permission, so hide the card
        canContact() {
            return this.memberPermissions.includes("Contact Us");
        },
    },
    created() {
        if (TokenService.getLocalAccessToken()) {
            MemberService.checkMemberPermissions().then(
                (response) => {
                    if (response.data.result.isSuccess) {
                        this.memberPermissions = response.data.data;
                    }
                },
                () => {}
            );
        }
    },
    // draws its own header like the other MBR screens: hide the top navbar and keep
    // the sidenav as an off-screen drawer
    beforeMount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.add("mbr-sidenav-overlay");
        app.classList.remove("g-sidenav-pinned");
    },
    mounted() {
        const code = getMerchantCode();
        this.merchantCode = code ? `/${code}` : "";
    },
    beforeUnmount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.remove("mbr-sidenav-overlay");
        if (window.innerWidth > 1200) {
            app.classList.add("g-sidenav-pinned");
        }
    },
    methods: {
        ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
        toggle(index) {
            this.openIndex = this.openIndex === index ? null : index;
        },
        goBack() {
            this.$router.push(this.merchantCode + "/account");
        },
        goContact() {
            this.$router.push(this.merchantCode + "/contact-us");
        },
    },
};
</script>

<style scoped>
.mbr-faq.page-header {
    overflow: visible;
    overflow-x: clip;
    font-family: var(--mbr-font-body);
    color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }`; icon fonts are left alone */
.mbr-faq :where(:not(.material-icons-round):not(.fas)) {
    font-family: var(--mbr-font-body);
}

.mbr-faq .mask {
    background: var(--mbr-color-canvas) !important;
    opacity: 1;
}

.mbr-faq .container {
    max-width: 440px;
    margin: 0 auto;
}

/* header */
.mbr-faq__bar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    padding: 24px 20px 12px;
    background: var(--mbr-nav-bg);
    backdrop-filter: blur(var(--mbr-nav-blur));
    -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-faq__back {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    background: var(--mbr-color-surface);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
    color: var(--mbr-color-primary);
    transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-faq__back:hover {
    background: var(--mbr-color-tint);
}

.mbr-faq__back:active {
    transform: scale(0.95);
}

.mbr-faq__back svg {
    width: 18px;
    height: 18px;
}

/* centred title, balanced against the 40px back button */
.mbr-faq__title {
    flex: 1;
    margin: 0;
    padding-right: 40px;
    text-align: center;
    font-family: var(--mbr-font-headline) !important;
    font-size: 22px;
    font-weight: 800;
    line-height: 28px;
    letter-spacing: -0.02em;
    color: var(--mbr-color-ink-strong);
}

.mbr-faq__body {
    padding: 4px 20px calc(120px + env(safe-area-inset-bottom));
}

.mbr-faq__intro {
    margin: 0 0 20px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    color: var(--mbr-color-ink-secondary);
}

/* accordion */
.mbr-faq__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.mbr-faq__item {
    background: var(--mbr-color-card);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-lg);
    box-shadow: var(--mbr-shadow-card);
    overflow: hidden;
    transition: border-color 0.2s ease;
}

.mbr-faq__item--open {
    border-color: var(--mbr-color-primary);
}

.mbr-faq__question {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 16px;
    background: transparent;
    border: 0;
    text-align: left;
    color: var(--mbr-color-ink-strong);
}

.mbr-faq__question:focus-visible {
    outline: 2px solid var(--mbr-color-primary);
    outline-offset: -2px;
    border-radius: var(--mbr-radius-lg);
}

.mbr-faq__question-text {
    font-size: var(--mbr-body-md-size);
    font-weight: 700;
    line-height: var(--mbr-body-md-line);
}

.mbr-faq__chevron {
    flex: none;
    width: 18px;
    height: 18px;
    color: var(--mbr-color-primary);
    transition: transform 0.2s ease;
}

.mbr-faq__item--open .mbr-faq__chevron {
    transform: rotate(180deg);
}

/* 0fr → 1fr animates to the answer's natural height */
.mbr-faq__answer {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.25s ease;
}

.mbr-faq__item--open .mbr-faq__answer {
    grid-template-rows: 1fr;
}

.mbr-faq__answer-inner {
    min-height: 0;
    overflow: hidden;
}

.mbr-faq__answer-text {
    margin: 0;
    padding: 0 16px 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    color: var(--mbr-color-ink-secondary);
}

/* still need help */
.mbr-faq__help {
    margin-top: 28px;
    padding: 20px;
    text-align: center;
    background: var(--mbr-color-tint);
    border-radius: var(--mbr-radius-xl);
}

.mbr-faq__help-title {
    margin: 0 0 4px;
    font-family: var(--mbr-font-headline) !important;
    font-size: var(--mbr-headline-sm-size);
    font-weight: 800;
    line-height: var(--mbr-headline-sm-line);
    letter-spacing: var(--mbr-headline-sm-tracking);
    color: var(--mbr-color-ink-strong);
}

.mbr-faq__help-text {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.6;
    color: var(--mbr-color-ink-secondary);
}

.mbr-faq__help-btn {
    width: 100%;
    height: 48px;
    background: var(--mbr-color-primary);
    border: 0;
    border-radius: var(--mbr-radius-full);
    font-size: 15px;
    font-weight: 700;
    color: var(--mbr-color-on-primary);
    transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-faq__help-btn:hover {
    background: var(--mbr-color-primary-light);
}

.mbr-faq__help-btn:active {
    transform: scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
    .mbr-faq__answer,
    .mbr-faq__chevron {
        transition: none;
    }
}
</style>
